/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAnswersCustomerDetail } from '@/core/domain/crm/Answers/Answers.hooks';
import {
  AnswerCustomer,
  AnswerCustomerQuestionOptionAnswer,
  ListAnswersParams,
} from '@/core/domain/crm/Answers/Answers.types';
import { useRouteMatchParam } from '@/hooks';
import {
  Checkbox,
  Collapse,
  Form,
  FormItemProps,
  Input,
  Modal,
  ModalProps,
  Radio,
  Rate,
  RateProps,
  Skeleton,
  Space,
} from 'antd';
import { RequestResponseQuestion } from '@/core/domain/crm/RequestResponse/RequestResponse.types';
import React, { useEffect, useState } from 'react';
import { Group } from '@/components/_common';
import { QuestionTypeEnum } from '@/core/domain/crm/Questions/Questions.types';
import moment from 'moment';
import StatusTag from '@/components/_common/StatusTag';

interface Props extends ModalProps {
  customer?: AnswerCustomer;
  params: ListAnswersParams;
}

const RateCustom = React.forwardRef<
  unknown,
  RateProps & { question: RequestResponseQuestion }
>(({ question, ...props }, ref) => {
  const form = Form.useFormInstance();

  return (
    <Group gap={10}>
      <span
        className="cursor-pointer"
        onClick={() => form.setFieldValue(question.id, 1)}
      >
        {question.lowerEnd}
      </span>

      <Rate {...props} ref={ref} />

      <span
        className="cursor-pointer"
        onClick={() => form.setFieldValue(question.id, 5)}
      >
        {question.topEnd}
      </span>
    </Group>
  );
});

interface DataItem {
  id: number;
  date: string;
  questions: (RequestResponseQuestion & {
    optionsAnswer: AnswerCustomerQuestionOptionAnswer[];
  })[];
}

const AnswerCustomerModal: React.FC<Props> = ({
  customer,
  params,
  ...props
}) => {
  const quizId = useRouteMatchParam('quizId');
  const [form] = Form.useForm();

  const [dataSource, setDataSource] = useState<DataItem[]>([]);

  const [collapseKeys, setCollapseKeys] = useState<string[]>([]);

  const { data, isLoading, dataUpdatedAt } = useAnswersCustomerDetail(
    { quizId: Number(quizId), customerId: customer?.id },
    params,
  );

  const getNameField = (question: RequestResponseQuestion) =>
    `${question.type}-${question.id}-${question.questionSequence}`;

  useEffect(() => {
    if (data) {
      let obj = {};

      data.forEach((item) => {
        if (item.question.type === QuestionTypeEnum.Classification) {
          obj = { ...obj, [getNameField(item.question)]: item.stars };
        }

        if (item.question.type === QuestionTypeEnum.Comments) {
          obj = { ...obj, [getNameField(item.question)]: item.comment };
        }

        if (item.question.type === QuestionTypeEnum.MultipleAnswers) {
          obj = {
            ...obj,
            [getNameField(item.question)]: item.selectedOptions.map(
              (opt) => opt.answerOption.id,
            ),
          };
        }

        if (item.question.type === QuestionTypeEnum.SingleAnswer) {
          obj = {
            ...obj,
            [getNameField(item.question)]: item.selectedOptions.map(
              (opt) => opt.answerOption.id,
            ),
          };
        }
      });

      form.setFieldsValue(obj);

      setDataSource(
        data.reduce<DataItem[]>((acc, curr) => {
          const finded = acc.find(
            (item) => item.id === curr.requestResponse.id,
          );

          if (finded) {
            return acc.map((item) =>
              item.id === curr.requestResponse.id
                ? {
                    ...item,
                    questions: [
                      ...item.questions,
                      { ...curr.question, optionsAnswer: curr.optionsAnswer },
                    ],
                  }
                : item,
            );
          }

          return [
            ...acc,
            {
              id: curr.requestResponse.id,
              date: curr.requestResponse.replyDate,
              questions: [
                { ...curr.question, optionsAnswer: curr.optionsAnswer },
              ],
            },
          ];
        }, []),
      );
    }
  }, [dataUpdatedAt]);

  const formItemProps = (
    question: RequestResponseQuestion,
    props?: FormItemProps,
  ): FormItemProps => ({
    name: getNameField(question),
    label: `${question.questionSequence || '-'}) ${question.description}`,
    className: 'crm-quiz-form-item',
    ...props,
  });

  function renderComment(question: RequestResponseQuestion) {
    return (
      <Form.Item {...formItemProps(question)}>
        <Input.TextArea disabled rows={4} />
      </Form.Item>
    );
  }

  function renderMultiple(question: RequestResponseQuestion) {
    return (
      <Form.Item {...formItemProps(question)}>
        <Checkbox.Group>
          {question.optionsAnswer
            ?.sort((a, b) => a.answerOptionSequence - b.answerOptionSequence)
            .map((option) => (
              <Checkbox disabled value={option.id}>
                {option.description}
              </Checkbox>
            ))}
        </Checkbox.Group>
      </Form.Item>
    );
  }

  function renderSingle(question: RequestResponseQuestion) {
    return (
      <Form.Item {...formItemProps(question)}>
        <Radio.Group>
          {question.optionsAnswer
            ?.sort((a, b) => a.answerOptionSequence - b.answerOptionSequence)
            .map((option) => (
              <Radio disabled value={option.id}>
                {option.description}
              </Radio>
            ))}
        </Radio.Group>
      </Form.Item>
    );
  }

  function renderClassification(question: RequestResponseQuestion) {
    return (
      <Form.Item
        {...formItemProps(question, {
          rules: [
            {
              type: 'number',
              min: 1,
              max: 5,
            },
          ],
        })}
      >
        <RateCustom disabled question={question} />
      </Form.Item>
    );
  }

  return (
    <Modal
      {...props}
      title={customer?.name}
      destroyOnClose
      okButtonProps={{ style: { display: 'none' } }}
      cancelText="Fechar"
    >
      <Skeleton loading={isLoading}>
        <Form layout="vertical" form={form}>
          <Collapse
            defaultActiveKey={[]}
            activeKey={collapseKeys}
            onChange={(keys) =>
              setCollapseKeys(
                Array.isArray(keys) ? [keys.at(-1) || ''] : [keys],
              )
            }
          >
            {dataSource?.map((item) => (
              <Collapse.Panel
                header={
                  <StatusTag
                    color="blue"
                    label={moment(item.date).format('DD/MM/YYYY')}
                  />
                }
                key={item.id}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {item.questions
                    .sort((a, b) => a.questionSequence - b.questionSequence)
                    .map((question, index) => (
                      <div key={index}>
                        {question.type === QuestionTypeEnum.Classification &&
                          renderClassification(question)}

                        {question.type === QuestionTypeEnum.Comments &&
                          renderComment(question)}

                        {question.type === QuestionTypeEnum.MultipleAnswers &&
                          renderMultiple(question)}

                        {question.type === QuestionTypeEnum.SingleAnswer &&
                          renderSingle(question)}
                      </div>
                    ))}
                </Space>
              </Collapse.Panel>
            ))}
          </Collapse>
        </Form>
      </Skeleton>
    </Modal>
  );
};

export default AnswerCustomerModal;
